<template>
  <div class="journeys-page">
    <page-hero
        class="mb-1"
        :title="msg('guestjourney')"
        :subtitle="msg('journeyintro')"
        variant="realestate"
        :stats="heroStats"
    >
      <template #illustration>
        <svg viewBox="0 0 100 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 95 C20 60, 80 60, 80 25" stroke="rgba(255,255,255,.35)" stroke-width="3"
                stroke-dasharray="6 6" fill="none"/>
          <circle cx="20" cy="95" r="8" fill="rgba(255,255,255,.4)"/>
          <circle cx="50" cy="72" r="6" fill="rgba(255,255,255,.25)"/>
          <circle cx="80" cy="25" r="9" fill="rgba(255,255,255,.45)"/>
        </svg>
      </template>
    </page-hero>

    <b-card no-body class="jl-card">
      <div class="jl-toolbar">
        <div class="jl-toolbar__left">
          <b-form-input v-model="search" class="jl-search" :placeholder="msg('Search')"/>
          <b-button variant="flat-secondary" size="sm" :to="'/journey/responses'">
            <feather-icon icon="InboxIcon" size="14" class="mr-25"/>{{ msg('journeyresponses') }}
          </b-button>
          <b-button variant="flat-secondary" size="sm" :to="'/journey/stats'">
            <feather-icon icon="BarChart2Icon" size="14" class="mr-25"/>{{ msg('journeystats') }}
          </b-button>
        </div>
        <b-button variant="primary" size="sm" @click="createJourney">
          <feather-icon icon="PlusIcon" size="14" class="mr-25"/>{{ msg('newjourney') }}
        </b-button>
      </div>

      <div v-if="loading" class="text-center text-primary my-3">
        <b-spinner class="align-middle mx-1"/>
        <strong>{{ msg('Loading') }}...</strong>
      </div>

      <div v-else-if="filtered.length === 0" class="jl-empty">
        <feather-icon icon="MapIcon" size="34" class="jl-empty__icon"/>
        <p class="jl-empty__title">{{ msg('guestjourney') }}</p>
        <p class="jl-empty__text">{{ msg('journeyemptyhint') }}</p>
        <b-button variant="primary" @click="createJourney">{{ msg('newjourney') }}</b-button>
      </div>

      <div v-else class="jl-grid">
        <div v-for="journey in filtered" :key="journey.id" class="jl-card-item"
             @click="edit(journey)">
          <div class="jl-card-item__head">
            <b-badge :variant="'light-' + scopeVariant(journey.scope)" class="jl-scope">
              <feather-icon :icon="scopeIcon(journey.scope)" size="11" class="mr-25"/>
              {{ msg(scopeLabel(journey.scope)) }}
            </b-badge>
            <b-form-checkbox switch :checked="journey.active === true" @click.native.stop
                             v-b-tooltip.hover :title="msg('info_activejourney')"
                             @change="toggle(journey)"/>
          </div>

          <div class="jl-card-item__name">{{ journey.name }}</div>
          <div class="jl-card-item__meta">
            <span><feather-icon icon="LayersIcon" size="12"/>{{ (journey.steps || []).length }} {{ msg('steps') }}</span>
            <span><feather-icon icon="HelpCircleIcon" size="12"/>{{ countQuestions(journey) }} {{ msg('questions') }}</span>
          </div>

          <div class="jl-card-item__actions" @click.stop>
            <b-button variant="flat-primary" size="sm" @click="edit(journey)">
              <feather-icon icon="Edit2Icon" size="13"/>
            </b-button>
            <b-button variant="flat-secondary" size="sm" @click="duplicate(journey)">
              <feather-icon icon="CopyIcon" size="13"/>
            </b-button>
            <b-button variant="flat-danger" size="sm" @click="confirmDelete(journey)">
              <feather-icon icon="Trash2Icon" size="13"/>
            </b-button>
          </div>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import {VBTooltip} from 'bootstrap-vue'
import PageHero from '@/components/PageHero'
import {JOURNEY_SCOPES} from '@/model/journey/journeyTypes'

export default {
  name: 'Journeys',
  components: {PageHero},
  directives: {'b-tooltip': VBTooltip},
  data() {
    return {
      loading: true,
      journeys: [],
      search: '',
    }
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    filtered() {
      const term = (this.search || '').toLowerCase().trim();
      if (!term) return this.journeys;
      return this.journeys.filter(journey => (journey.name || '').toLowerCase().includes(term));
    },
    heroStats() {
      return [
        {icon: 'MapIcon', value: this.journeys.length, label: this.msg('journeys')},
        {icon: 'CheckCircleIcon', value: this.journeys.filter(j => j.active).length, label: this.msg('Active')},
      ];
    },
  },
  created() {
    this.refresh();
  },
  methods: {
    refresh() {
      const $this = this;
      this.loading = true;
      this.$store.dispatch('journey/getAll', {amount: 200})
          .then(ans => {
            $this.journeys = ans['Journeys'] || [];
          })
          .finally(() => $this.loading = false);
    },
    countQuestions(journey) {
      return (journey.steps || []).reduce((sum, step) => sum + (step.questions || []).length, 0);
    },
    scopeMeta(scope) {
      return JOURNEY_SCOPES.find(item => item.value === scope) || JOURNEY_SCOPES[0];
    },
    scopeLabel(scope) {
      return this.scopeMeta(scope).label;
    },
    scopeIcon(scope) {
      return this.scopeMeta(scope).icon;
    },
    scopeVariant(scope) {
      return this.scopeMeta(scope).variant;
    },
    createJourney() {
      this.$router.push('/journey/edit/new');
    },
    edit(journey) {
      this.$router.push('/journey/edit/' + journey.id);
    },
    toggle(journey) {
      const $this = this;
      this.$store.dispatch('journey/toggle', journey.id).then(() => $this.refresh());
    },
    duplicate(journey) {
      const $this = this;
      this.$store.dispatch('journey/duplicate', journey.id).then(() => $this.refresh());
    },
    confirmDelete(journey) {
      const $this = this;
      this.$bvModal.msgBoxConfirm(this.msg('journeydeleteconfirm'), {
        title: this.msg('Confirm deletion'),
        okVariant: 'danger',
        okTitle: this.msg('Yes, Delete'),
        cancelTitle: this.msg('cancel'),
        centered: true,
      }).then(confirmed => {
        if (confirmed !== true) return;
        $this.$store.dispatch('journey/remove', journey.id).then(() => $this.refresh());
      });
    },
  }
}
</script>

<style lang="scss" scoped>
.jl-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 12px 18px;
  border-bottom: 1px solid rgba(34, 41, 47, .08);

  &__left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
}

.jl-search {
  width: 230px;
}

.jl-empty {
  text-align: center;
  padding: 3rem 1rem;

  &__icon {
    color: #b9b9c3;
    margin-bottom: .75rem;
  }

  &__title {
    font-weight: 600;
    margin-bottom: .25rem;
  }

  &__text {
    color: #6e6b7b;
    max-width: 460px;
    margin: 0 auto 1rem;
  }
}

.jl-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
  gap: 14px;
  padding: 18px;
}

.jl-card-item {
  position: relative;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid rgba(34, 41, 47, .1);
  background: #fff;
  cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(34, 41, 47, .12);
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  &__name {
    font-weight: 700;
    font-size: 15px;
    color: #5e5873;
    margin-bottom: 6px;
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    color: #6e6b7b;
    font-size: 12.5px;

    span {
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }
  }

  &__actions {
    display: flex;
    gap: 2px;
    margin-top: 10px;
    border-top: 1px solid rgba(34, 41, 47, .07);
    padding-top: 6px;
  }
}

.dark-layout {
  .jl-toolbar {
    border-bottom-color: rgba(255, 255, 255, .08);
  }

  .jl-card-item {
    background: #283046;
    border-color: rgba(255, 255, 255, .08);

    &__name {
      color: #d0d2d6;
    }

    &__meta {
      color: #b4b7bd;
    }

    &__actions {
      border-top-color: rgba(255, 255, 255, .07);
    }
  }

  .jl-empty__text {
    color: #b4b7bd;
  }
}
</style>
